import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Textarea } from '../ui/textarea';
import {
  Star, ShieldCheck, TrendingUp, Award, UserPlus, UserMinus,
  UserCheck, Loader2, AlertTriangle, Banknote, UserX,
  Mail, Phone, Calendar, Clock, CheckCircle2, XCircle, Shield, Info, MapPin,
  StickyNote, Plus, Pencil, Trash2, Save
} from 'lucide-react';

const badgeStyles = {
  verified: { bg: 'bg-emerald-100 text-emerald-800', icon: ShieldCheck },
  reliable: { bg: 'bg-blue-100 text-blue-800', icon: TrendingUp },
  top_rated: { bg: 'bg-amber-100 text-amber-800', icon: Award },
  no_show_flag: { bg: 'bg-red-100 text-red-800', icon: UserX },
  till_issue_flag: { bg: 'bg-orange-100 text-orange-800', icon: Banknote },
  high_risk: { bg: 'bg-red-100 text-red-800', icon: AlertTriangle },
};

const shiftStatusStyles = {
  completed: 'bg-emerald-100 text-emerald-800',
  assigned: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-gray-100 text-gray-600',
  no_show: 'bg-red-100 text-red-800',
  open: 'bg-sky-100 text-sky-800',
};

const penaltyStatusStyles = {
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-800',
  waived: 'bg-slate-100 text-slate-600',
};

const formatDate = (str) => {
  if (!str) return '-';
  return new Date(str).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export function WorkerProfileModal({ open, onClose, profileData, profileLoading, onVerify, onDeactivate, onReactivate, onRemove, actionLoading, showStaffActions = true, viewerRole = 'manager', api, token }) {
  const [profileTab, setProfileTab] = useState('overview');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setProfileTab('overview'); } }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        {profileLoading ? (
          <div className="p-8 space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : profileData ? (
          <WorkerProfileView
            data={profileData}
            onVerify={onVerify}
            onDeactivate={onDeactivate}
            onReactivate={onReactivate}
            onRemove={onRemove}
            actionLoading={actionLoading}
            profileTab={profileTab}
            setProfileTab={setProfileTab}
            showStaffActions={showStaffActions}
            viewerRole={viewerRole}
            api={api}
            token={token}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function WorkerProfileView({ data, onVerify, onDeactivate, onReactivate, onRemove, actionLoading, profileTab, setProfileTab, showStaffActions, viewerRole = 'manager', api, token }) {
  const { worker, confirmerName, trustStatus, stats, badges, penalties, ratings, recentShifts, staffLink } = data;
  const isActive = staffLink?.isActive !== false;
  const isManagerView = viewerRole === 'manager';
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const getPhotoUrl = (path) => {
    if (!path) return null;
    return `${API_URL}/api/files/${path}?auth=${token}`;
  };

  const trustStyles = {
    trusted: { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' },
    use_caution: { bg: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
    high_risk: { bg: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500' },
  };

  return (
    <div data-testid="worker-profile-modal">
      {/* Header with Profile Picture */}
      <div className="p-6 pb-4 border-b border-slate-100">
        <div className="flex items-start gap-4">
          {worker.profilePhoto ? (
            <img src={getPhotoUrl(worker.profilePhoto)} alt={worker.fullName} className="h-16 w-16 rounded-full object-cover shrink-0 border-2 border-white shadow-md" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] flex items-center justify-center text-white text-2xl font-bold shrink-0">
              {worker.fullName?.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-slate-900">{worker.fullName}</h2>
              {staffLink && !isActive && <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>}
              {isManagerView && trustStatus && (
                <Badge className={`${trustStyles[trustStatus.level]?.bg || 'bg-slate-100'} gap-1.5 text-xs font-semibold border`} data-testid="trust-status-badge">
                  <span className={`h-2 w-2 rounded-full ${trustStyles[trustStatus.level]?.dot || 'bg-slate-400'}`} />
                  {trustStatus.label}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {worker.email}</span>
              {worker.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {worker.phone}</span>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {badges.map(b => {
                const style = badgeStyles[b.key] || { bg: 'bg-slate-100 text-slate-700', icon: Shield };
                const Icon = style.icon;
                return (
                  <VerifiedBadge key={b.key} badge={b} style={style} Icon={Icon} worker={worker} confirmerName={confirmerName} />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-px bg-slate-100">
        <StatCell label="Rating" value={
          <span className="flex items-center gap-1 text-amber-500"><Star className="h-4 w-4 fill-current" />{(worker.ratingAverage || 0).toFixed(1)}</span>
        } />
        <StatCell label="Reliability" value={
          <span className={`font-bold ${worker.reliabilityScore >= 85 ? 'text-emerald-600' : worker.reliabilityScore >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
            {(worker.reliabilityScore || 100).toFixed(0)}%
          </span>
        } />
        <StatCell label="Completed" value={<span className="font-bold text-slate-900">{stats.completedShifts}</span>} />
        <StatCell label={isManagerView ? "Issues" : "Penalties"} value={<span className={`font-bold ${stats.totalPenalties > 0 ? 'text-red-600' : 'text-slate-900'}`}>{stats.totalPenalties}</span>} />
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <Tabs value={profileTab} onValueChange={setProfileTab}>
          <TabsList className="bg-slate-100 p-1.5 rounded-xl w-full h-auto flex-wrap gap-1">
            <TabsTrigger value="overview" className="flex-1 rounded-lg text-sm py-2.5 font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="shifts" className="flex-1 rounded-lg text-sm py-2.5 font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-shifts">Shifts</TabsTrigger>
            <TabsTrigger value="ratings" className="flex-1 rounded-lg text-sm py-2.5 font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-ratings">Ratings ({ratings.length})</TabsTrigger>
            {isManagerView && (
              <TabsTrigger value="notes" className="flex-1 rounded-lg text-sm py-2.5 font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="notes-tab">
                Notes
              </TabsTrigger>
            )}
            <TabsTrigger value="penalties" className="flex-1 rounded-lg text-sm py-2.5 font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-penalties">
              {isManagerView ? 'Behaviour' : `Penalties (${penalties.length})`}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-4 space-y-4">
        {profileTab === 'overview' && (
          <>
            <Section title="Verification">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {worker.smartIdVerified || worker.smartIdManagerConfirmed ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-slate-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {worker.smartIdManagerConfirmed ? 'HR Confirmed' : worker.smartIdVerified ? 'Smart ID Verified' : worker.smartIdSelfDeclared ? 'Self-Declared Only' : 'Not Verified'}
                      </p>
                      {confirmerName && (
                        <p className="text-xs text-slate-500">Confirmed by {confirmerName} on {formatDate(worker.smartIdConfirmedAt)}</p>
                      )}
                      {worker.smartIdConfirmedBranch && (
                        <p className="text-xs text-slate-500">Branch: {worker.smartIdConfirmedBranch}</p>
                      )}
                    </div>
                  </div>
                  {onVerify && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs h-8"
                      onClick={() => onVerify(worker.id)} disabled={actionLoading === 'verify-' + worker.id}
                      data-testid="toggle-verify-btn"
                    >
                      {actionLoading === 'verify-' + worker.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                      {worker.smartIdVerified ? 'Re-verify' : 'Verify'}
                    </Button>
                  )}
                </div>
                {/* HR Confirmation Prompt - Always visible for managers */}
                {isManagerView && (
                  <div className="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg" data-testid="profile-hr-prompt">
                    <Phone className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-amber-700 font-medium">Please call HR to confirm Smart ID before approving.</p>
                      {worker.smartIdManagerConfirmed && worker.smartIdConfirmedBranch && (
                        <p className="text-xs text-amber-600 mt-0.5" data-testid="profile-hr-previously-verified">
                          Previously verified by {worker.smartIdConfirmedBranch}{confirmerName ? ` (${confirmerName})` : ''}, but HR confirmation is still required before approval.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <InfoRow label="Role" value={worker.preferredRole || worker.role || '-'} />
                  <InfoRow label="Experience" value={worker.experienceLevel || '-'} />
                  <InfoRow label="Status" value={staffLink ? (isActive ? 'Active' : 'Deactivated') : 'Not in staff'} />
                  <InfoRow label="Preferred Area" value={worker.preferredArea || '-'} />
                  <InfoRow label="Joined" value={formatDate(worker.createdAt)} />
                </div>
              </div>
            </Section>

            <Section title="Accountability">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniStat label="Cancellations" value={stats.cancelledShifts} color={stats.cancelledShifts > 0 ? 'text-amber-600' : 'text-slate-900'} />
                <MiniStat label="No-Shows" value={stats.noShowCount} color={stats.noShowCount > 0 ? 'text-red-600' : 'text-slate-900'} />
                <MiniStat label="Till Issues" value={stats.tillDiscrepancies} color={stats.tillDiscrepancies > 0 ? 'text-orange-600' : 'text-slate-900'} />
                <MiniStat label="Pending Issues" value={stats.pendingPenalties} color={stats.pendingPenalties > 0 ? 'text-red-600' : 'text-slate-900'} />
              </div>
            </Section>

            {showStaffActions && staffLink && (
              <Section title="Actions">
                <div className="flex flex-wrap gap-2">
                  {isActive ? (
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs h-8"
                      onClick={(e) => onDeactivate?.(staffLink.id, e)} disabled={actionLoading === staffLink.id}
                      data-testid="profile-deactivate-btn"
                    >
                      {actionLoading === staffLink.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserMinus className="h-3.5 w-3.5 mr-1" />} Deactivate
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-full text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs h-8"
                      onClick={(e) => onReactivate?.(staffLink.id, e)} disabled={actionLoading === staffLink.id}
                      data-testid="profile-reactivate-btn"
                    >
                      {actionLoading === staffLink.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserCheck className="h-3.5 w-3.5 mr-1" />} Reactivate
                    </Button>
                  )}
                  {onRemove && (
                    <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-700 text-white text-xs h-8"
                      onClick={() => setShowRemoveConfirm(true)}
                      disabled={actionLoading === 'remove-active-' + staffLink.id}
                      data-testid="profile-remove-worker-btn"
                    >
                      {actionLoading === 'remove-active-' + staffLink.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserX className="h-3.5 w-3.5 mr-1" />} Remove Worker
                    </Button>
                  )}
                </div>

                {/* Remove Worker Confirmation */}
                {showRemoveConfirm && (
                  <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-xl animate-in slide-in-from-top-2 duration-200" data-testid="remove-worker-confirm">
                    <h4 className="text-sm font-bold text-red-900 mb-2">Remove Worker?</h4>
                    <p className="text-sm text-red-800 mb-4">
                      Are you sure you want to remove this worker from your staff list? This will only remove them from your manager list. Their profile, shift history, ratings, and audit records will not be deleted.
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-full text-xs h-8"
                        onClick={() => setShowRemoveConfirm(false)}
                        data-testid="cancel-remove-worker-btn"
                      >
                        Cancel
                      </Button>
                      <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-700 text-white text-xs h-8"
                        onClick={() => {
                          setShowRemoveConfirm(false);
                          onRemove?.(staffLink.id, worker);
                        }}
                        disabled={actionLoading === 'remove-active-' + staffLink.id}
                        data-testid="confirm-remove-worker-btn"
                      >
                        {actionLoading === 'remove-active-' + staffLink.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserX className="h-3.5 w-3.5 mr-1" />}
                        Remove Worker
                      </Button>
                    </div>
                  </div>
                )}
              </Section>
            )}
          </>
        )}

        {profileTab === 'shifts' && (
          <Section title="Recent Shifts">
            {recentShifts.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No shift history</p>
            ) : (
              <div className="space-y-2">
                {recentShifts.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`shift-row-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{s.branchName}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {s.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.location}</span>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(s.date)}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {s.scheduledStartTime} - {s.scheduledEndTime}</span>
                        </div>
                      </div>
                    </div>
                    <Badge className={`${shiftStatusStyles[s.status] || 'bg-slate-100'} text-xs`}>{s.status?.replace('_', ' ')}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {profileTab === 'ratings' && (
          <Section title="Rating History">
            {ratings.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No ratings yet</p>
            ) : (
              <div className="space-y-3">
                {ratings.map(r => (
                  <div key={r.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`rating-row-${r.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star key={i} className={`h-4 w-4 ${i <= r.ratingValue ? 'text-amber-400 fill-current' : 'text-slate-300'}`} />
                        ))}
                      </div>
                      <span className="text-xs text-slate-400">{formatDate(r.createdAt)}</span>
                    </div>
                    {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                    <p className="text-xs text-slate-400 mt-1">by {r.managerName}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {profileTab === 'notes' && isManagerView && api && (
          <ManagerNotesSection workerId={worker.id} api={api} />
        )}

        {profileTab === 'penalties' && (
          isManagerView ? (
            /* Manager View: Behaviour counts only, no amounts */
            <Section title="Worker Behaviour">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <MiniStat label="Cancellations" value={stats.cancelledShifts} color={stats.cancelledShifts > 0 ? 'text-amber-600' : 'text-slate-900'} />
                <MiniStat label="Late Cancellations" value={stats.lateCancellations || 0} color={(stats.lateCancellations || 0) > 0 ? 'text-orange-600' : 'text-slate-900'} />
                <MiniStat label="No-Shows" value={stats.noShowCount} color={stats.noShowCount > 0 ? 'text-red-600' : 'text-slate-900'} />
                <MiniStat label="Till Issues" value={stats.tillDiscrepancies} color={stats.tillDiscrepancies > 0 ? 'text-orange-600' : 'text-slate-900'} />
              </div>
              {penalties.length > 0 ? (
                <div className="space-y-2">
                  {penalties.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`behaviour-row-${p.id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{p.type?.replace('_', ' ')}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{p.reason}</p>
                      </div>
                      <span className="text-xs text-slate-400">{formatDate(p.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No behaviour issues recorded</p>
              )}
            </Section>
          ) : (
            /* Worker View: Full penalty details with amounts */
            <Section title="Penalty History">
              {penalties.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No penalties</p>
              ) : (
                <div className="space-y-2">
                  {penalties.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`penalty-row-${p.id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">&pound;{p.amount?.toFixed(2)} - {p.type?.replace('_', ' ')}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{p.reason}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{formatDate(p.createdAt)}</span>
                        <Badge className={`${penaltyStatusStyles[p.penaltyStatus] || 'bg-slate-100'} text-xs`}>{p.penaltyStatus}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )
        )}
      </div>
    </div>
  );
}

function VerifiedBadge({ badge, style, Icon, worker, confirmerName }) {
  if (badge.key === 'verified') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center cursor-pointer" data-testid="verified-badge-clickable">
            <Badge className={`${style.bg} gap-1 text-xs hover:opacity-80 transition-opacity`}>
              <Icon className="h-3 w-3" /> {badge.label}
              <Info className="h-2.5 w-2.5 ml-0.5 opacity-60" />
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" side="bottom" align="start">
          <h4 className="text-sm font-semibold text-slate-900 mb-2">Verification Details</h4>
          <div className="space-y-1.5 text-sm">
            {worker.smartIdConfirmedBranch && (
              <p className="text-slate-600"><span className="text-slate-400">Branch:</span> {worker.smartIdConfirmedBranch}</p>
            )}
            {confirmerName && (
              <p className="text-slate-600"><span className="text-slate-400">Verified by:</span> {confirmerName}</p>
            )}
            {worker.smartIdConfirmedAt && (
              <p className="text-slate-600"><span className="text-slate-400">Date:</span> {formatDate(worker.smartIdConfirmedAt)}</p>
            )}
            {!worker.smartIdManagerConfirmed && (
              <p className="text-amber-600 text-xs mt-1">Self-declared only. HR confirmation pending.</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Badge className={`${style.bg} gap-1 text-xs`} title={badge.description} data-testid={`badge-${badge.key}`}>
      <Icon className="h-3 w-3" /> {badge.label}
    </Badge>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{title}</h3>
      {children}
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="bg-white p-3 text-center">
      <div className="text-lg">{value}</div>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="p-2.5 bg-slate-50 rounded-lg">
      <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-slate-700 mt-0.5">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, color = 'text-slate-900' }) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg text-center">
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}

function ManagerNotesSection({ workerId, api }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await api.get(`/staff/worker/${workerId}/notes`);
      setNotes(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [api, workerId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleCreate = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      await api.post(`/staff/worker/${workerId}/notes`, { content: newNote.trim() });
      setNewNote('');
      fetchNotes();
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (noteId) => {
    if (!editContent.trim()) return;
    setEditSaving(true);
    try {
      await api.patch(`/staff/notes/${noteId}`, { content: editContent.trim() });
      setEditingId(null);
      fetchNotes();
    } catch {
      // silently fail
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    setDeletingId(noteId);
    try {
      await api.delete(`/staff/notes/${noteId}`);
      fetchNotes();
    } catch {
      // silently fail
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="py-6 text-center text-sm text-slate-400">Loading notes...</div>;
  }

  return (
    <Section title="Private Manager Notes">
      <div className="space-y-3">
        {/* Create new note */}
        <div className="flex gap-2">
          <Textarea
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder="Write a private note about this worker..."
            rows={2}
            className="rounded-lg resize-none text-sm flex-1"
            data-testid="new-note-input"
          />
          <Button
            size="sm"
            className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white h-10 px-4 shrink-0 self-end"
            onClick={handleCreate}
            disabled={saving || !newNote.trim()}
            data-testid="save-note-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>

        {/* Existing notes */}
        {notes.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No notes yet. Add your first note above.</p>
        ) : (
          <div className="space-y-2">
            {notes.map(note => (
              <div key={note.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`note-${note.id}`}>
                {editingId === note.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      rows={2}
                      className="rounded-lg resize-none text-sm"
                      data-testid="edit-note-input"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button size="sm" className="rounded-full text-xs h-7 bg-[#0f1b3d]" onClick={() => handleUpdate(note.id)} disabled={editSaving}>
                        {editSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />} Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-400">{formatDate(note.createdAt)}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => { setEditingId(note.id); setEditContent(note.content); }}
                          className="p-1 rounded hover:bg-slate-200 transition-colors"
                          data-testid={`edit-note-${note.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5 text-slate-400" />
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          disabled={deletingId === note.id}
                          className="p-1 rounded hover:bg-red-100 transition-colors"
                          data-testid={`delete-note-${note.id}`}
                        >
                          {deletingId === note.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-red-400" /> : <Trash2 className="h-3.5 w-3.5 text-red-400" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
